import { useState, useEffect } from 'react';
import { showErrorMessage, showSuccessMessage } from '@/shared/components/toast-system';
import {
  changeSubscription,
  createSubscriptionTransaction,
} from '../../../utils/subscriptionApiUtils';
import Button from '@/shared/components/Button';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import { _t } from '@/i18n/t';
import { Check } from 'lucide-react';
import {
  isPaddle,
  isStripe,
  isFreePlanName,
  openCenteredPopup,
  closePopup,
  startCheckoutOverlay,
  getResponseMessage,
  SUBSCRIPTION_MIN_WIDTH,
  type PlanType,
  type SubscriptionInfoType,
  type OriginalStateType,
} from '../subscriptionShared';

type ChangeAction = 'Upgrade' | 'Downgrade';

interface ChangeCopy {
  title: string;
  success: string;
  error: string;
  failed: string;
}

const CHANGE_COPY: Record<ChangeAction, ChangeCopy> = {
  Upgrade: {
    title: 'settings.subscription.changeDialog.upgradeTitle',
    success: 'settings.subscription.toast.upgraded',
    error: 'settings.subscription.toast.upgradeError',
    failed: 'settings.subscription.toast.upgradeFailed',
  },
  Downgrade: {
    title: 'settings.subscription.changeDialog.downgradeTitle',
    success: 'settings.subscription.toast.downgraded',
    error: 'settings.subscription.toast.downgradeError',
    failed: 'settings.subscription.toast.downgradeFailed',
  },
};

const CARD_BASE_CLASS =
  'rounded-md border bg-card p-6 flex flex-col overflow-hidden transition-all duration-300 shadow-sm hover:-translate-y-1 hover:shadow-lg';

const CYCLE_BUTTON_BASE_CLASS =
  'inline-flex items-center justify-center whitespace-nowrap rounded-[calc(var(--radius,4px)-2px)] px-4 py-1.5 text-sm font-medium cursor-pointer transition-all border-none';

const CYCLE_BUTTON_SELECTED_CLASS = 'bg-card text-foreground shadow';
const CYCLE_BUTTON_IDLE_CLASS =
  'bg-transparent text-muted-foreground hover:text-foreground';

interface SubscribeProps {
  plans: PlanType[];
  subscriptionInfo: SubscriptionInfoType | undefined;
  isCheckoutReady: boolean;
  isOverlay?: boolean;
  onPaddleCheckout: (transactionId: string) => void;
  onStripeCheckout: (clientSecret: string, transactionId: string) => void;
  onHostedInvoiceRequired: (
    popup: Window,
    hostedUrl: string,
    subscriptionId: string,
    originalState: OriginalStateType | null
  ) => Promise<void>;
}

const Subscribe = ({
  plans,
  subscriptionInfo,
  isCheckoutReady,
  isOverlay = false,
  onPaddleCheckout,
  onStripeCheckout,
  onHostedInvoiceRequired,
}: SubscribeProps) => {
  const isExtendedTrial = !!subscriptionInfo?.isExtendedTrial;

  const [isProcessing, setIsProcessing] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<string>('month');
  const [showChangeDialog, setShowChangeDialog] = useState(false);
  const [changeDialogProps, setChangeDialogProps] = useState({
    title: '',
    description: '',
    onConfirm: () => {},
  });

  // Plans arrive after mount, so this effect — not a lazy useState initialiser —
  // is what actually selects the active plan's billing cycle.
  useEffect(() => {
    if (plans?.length > 0 && subscriptionInfo?.planName && subscriptionInfo?.status !== 'canceled') {
      const activePlan = plans.find((p) => p.name === subscriptionInfo.planName);
      if (activePlan) {
        setBillingCycle(activePlan.billingCycle);
      }
    }
  }, [plans, subscriptionInfo]);

  const currentPlanName = subscriptionInfo?.planName;
  const hasPaidPlan = !isFreePlanName(currentPlanName) && !isExtendedTrial;
  const filteredPlans = (plans || []).filter((plan) => plan.billingCycle === billingCycle);
  const isAnnual = billingCycle === 'year';

  const handleActionClick = (plan: PlanType) => {
    const currentPlan = plans.find((p) => p.name === currentPlanName);
    const isReSubscribing = !subscriptionInfo || subscriptionInfo.status === 'canceled';

    let action: ChangeAction | null = null;
    if (currentPlan) {
      if (parseFloat(plan.amount) > parseFloat(currentPlan.amount)) {
        action = 'Upgrade';
      } else if (parseFloat(plan.amount) < parseFloat(currentPlan.amount)) {
        action = 'Downgrade';
      }
    }

    if (action === null || isReSubscribing) {
      executeSubscribe(plan);
      return;
    }

    const changeAction = action;
    setChangeDialogProps({
      title: _t(CHANGE_COPY[changeAction].title),
      description: _t('settings.subscription.changeDialog.description', {
        plan: plan.name,
      }),
      onConfirm: () => {
        setShowChangeDialog(false);
        executeUpdateOrDowngrade(plan, changeAction);
      },
    });
    setShowChangeDialog(true);
  };

  const executeSubscribe = async (plan: PlanType) => {
    setIsProcessing(plan.id);
    try {
      const response = await createSubscriptionTransaction(plan.id);
      const result = response.data;

      if (isPaddle) {
        if (result.success && result.transactionId) {
          onPaddleCheckout(result.transactionId);
        } else {
          showErrorMessage(_t('settings.subscription.toast.subscribeInitError'));
        }
      } else if (isStripe) {
        if (result.success && result.clientSecret) {
          onStripeCheckout(result.clientSecret, result.transactionId);
        } else {
          showErrorMessage(_t('settings.subscription.toast.subscribeInitError'));
        }
      }
    } catch (err) {
      console.error('Subscription error:', err);
      showErrorMessage(_t('settings.subscription.toast.subscribeStartFailed'));
    } finally {
      setIsProcessing(null);
    }
  };

  const executeUpdateOrDowngrade = async (plan: PlanType, action: ChangeAction) => {
    setIsProcessing(plan.id);
    const copy = CHANGE_COPY[action];

    // Opened up front so it survives the popup blocker; Stripe may need it for 3DS.
    let popup: Window | null = null;
    let endOverlay: (() => void) | null = null;
    if (isStripe) {
      popup = openCenteredPopup('stripe-3ds-confirm');
      if (popup) {
        endOverlay = startCheckoutOverlay(
          _t('settings.subscription.overlay.paymentWindow')
        );
      }
    }

    try {
      const response = await changeSubscription(plan.id);
      const result = response.data;

      if (result.success) {
        closePopup(popup);
        showSuccessMessage(_t(copy.success));
        return;
      }

      if (result.paymentRequired && result.hostedUrl) {
        if (!popup || popup.closed) {
          console.warn('Popup blocked or closed; falling back to redirect');
          window.location.href = result.hostedUrl;
          return;
        }
        await onHostedInvoiceRequired(
          popup,
          result.hostedUrl,
          result.subscriptionId,
          result.originalState || null
        );
        return;
      }

      closePopup(popup);
      showErrorMessage(_t(copy.error));
    } catch (err) {
      closePopup(popup);
      showErrorMessage(getResponseMessage(err) || _t(copy.failed));
    } finally {
      endOverlay?.();
      setIsProcessing(null);
    }
  };

  const getPlanFeatures = (plan: PlanType): string[] => [
    _t(
      plan.content === 1
        ? 'settings.subscription.plan.features.contentOne'
        : 'settings.subscription.plan.features.contentOther',
      { count: plan.content }
    ),
    _t(
      plan.aiImages === 1
        ? 'settings.subscription.plan.features.aiImageOne'
        : 'settings.subscription.plan.features.aiImageOther',
      { count: plan.aiImages }
    ),
  ];

  const formatPrice = (amount: string): string => {
    const numAmount = parseFloat(amount);
    const value = isAnnual ? numAmount / 12 : numAmount;
    return value % 1 === 0 ? value.toFixed(0) : value.toFixed(2);
  };

  const cycleButtonClass = (selected: boolean): string => {
    const state = selected ? CYCLE_BUTTON_SELECTED_CLASS : CYCLE_BUTTON_IDLE_CLASS;
    return `${CYCLE_BUTTON_BASE_CLASS} ${state}`;
  };

  return (
    <>
      <ConfirmDialog
        open={showChangeDialog}
        onOpenChange={setShowChangeDialog}
        onConfirm={changeDialogProps.onConfirm}
        title={changeDialogProps.title}
        description={changeDialogProps.description}
        confirmLabel={_t('settings.subscription.changeDialog.confirm')}
        confirmVariant="primary"
        cancelLabel={_t('kb.actions.cancel')}
        elevated={isOverlay}
      />

      <div
        className="flex flex-col items-center gap-2 mb-8"
        style={{ minWidth: SUBSCRIPTION_MIN_WIDTH }}
      >
        <div className="inline-flex h-10 items-center justify-center rounded-md bg-muted p-0.5 border border-border">
          <button
            data-th-button="true"
            onClick={() => setBillingCycle('month')}
            className={cycleButtonClass(!isAnnual)}
          >
            {_t('settings.subscription.billing.monthly')}
          </button>
          <button
            data-th-button="true"
            onClick={() => setBillingCycle('year')}
            className={cycleButtonClass(isAnnual)}
          >
            {_t('settings.subscription.billing.annual')}
          </button>
        </div>
        <p className="text-xs text-muted-foreground m-0">
          {isAnnual
            ? _t('settings.subscription.billing.annualNote')
            : _t('settings.subscription.billing.monthlyNote')}
        </p>
      </div>

      <div
        className="grid gap-5 w-full"
        style={{ gridTemplateColumns: 'repeat(3, minmax(260px, 1fr))' }}
      >
        {filteredPlans.map((plan) => {
          const isCurrentPlan =
            plan.name === currentPlanName && subscriptionInfo?.status !== 'canceled';
          const displayPrice = formatPrice(plan.amount);
          const borderClass = isCurrentPlan ? 'border-border/60' : 'border-border';
          const cardClass = `${CARD_BASE_CLASS} ${borderClass}`;

          return (
            <div key={plan.id} className={cardClass}>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-sm font-semibold text-foreground m-0">{plan.name}</h3>
              </div>

              <div className="flex items-baseline mb-1">
                <span className="text-3xl font-bold text-foreground">${displayPrice}</span>
                <span className="text-sm text-muted-foreground ml-1">
                  {_t('settings.subscription.plan.perMonth')}
                </span>
              </div>

              {isAnnual ? (
                <p className="text-xs text-muted-foreground m-0 mb-5">
                  {_t('settings.subscription.plan.billedAnnually')}
                </p>
              ) : (
                <div className="h-5 mb-5" />
              )}

              <ul className="list-none p-0 m-0 mb-6 flex-1 flex flex-col gap-2.5">
                {getPlanFeatures(plan).map((feature, idx) => (
                  <li
                    key={idx}
                    className="flex items-center gap-2 text-sm text-muted-foreground"
                  >
                    <Check className="size-4 text-primary shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              <div className="mt-auto min-w-0">
                {isCurrentPlan ? (
                  <>
                    <div className="flex justify-center items-center h-9">
                      <span className="inline-flex items-center justify-center h-7 px-4 rounded-full bg-emerald-50 text-emerald-600 text-sm font-medium">
                        {_t('settings.subscription.plan.currentPlan')}
                      </span>
                    </div>
                    {/* Keeps card heights aligned with the non-current variant. */}
                    <p className="text-xs mt-1.5 invisible select-none" aria-hidden="true">
                      &nbsp;
                    </p>
                  </>
                ) : (
                  <>
                    <Button
                      className="w-full"
                      onClick={() => handleActionClick(plan)}
                      disabled={!isCheckoutReady || !!isProcessing}
                    >
                      {isProcessing === plan.id
                        ? _t('settings.subscription.processing')
                        : _t(
                            hasPaidPlan
                              ? 'settings.subscription.plan.changeTo'
                              : 'settings.subscription.plan.subscribeTo',
                            { plan: plan.name }
                          )}
                    </Button>
                    <p className="text-xs text-muted-foreground text-center mt-1.5">
                      {_t('settings.subscription.plan.prorated')}
                    </p>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default Subscribe;